import { useState } from "react";
import { Building2, Home, Plus, ChevronRight, X, AlertCircle, Briefcase } from "lucide-react";
import axios, { API } from "@/utils/api";

const INITIAL_FORM = {
  company_name: "", address: "", city: "", zip_code: "",
  country: "", contact_name: "", phone: "", email: "", website: ""
};

const ClientSelector = ({ clients, selectedClient, onSelectClient, onCreateClient, user }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState("commercial");
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const commercialClients = clients.filter(c => (c.client_type || "commercial") === "commercial");
  const residentialClients = clients.filter(c => c.client_type === "residential");

  const openCreate = (type) => {
    setCreateType(type);
    setFormData(INITIAL_FORM);
    setError("");
    setShowCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/clients`, { ...formData, client_type: createType });
      onCreateClient(response.data);
      setShowCreateModal(false);
    } catch (error) {
      setError(error.response?.data?.detail || "Failed to create client.");
    } finally {
      setLoading(false);
    }
  };

  const renderClientGrid = (clientList, type) => {
    if (clientList.length === 0) {
      const Icon = type === "commercial" ? Building2 : Home;
      return (
        <div className="card" style={{ opacity: 0.7 }}>
          <div className="flex-center" style={{ padding: "var(--space-xl)", color: "var(--text-muted)" }}>
            <div style={{ textAlign: "center" }}>
              <Icon size={36} strokeWidth={1} />
              <p className="mt-sm" style={{ fontSize: "var(--text-small)" }}>
                No {type} clients yet
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="client-list">
        {clientList.map((client) => (
          <div
            key={client.id}
            className={`client-card ${selectedClient?.id === client.id ? "selected" : ""}`}
            onClick={() => onSelectClient(client)}
            data-testid={`client-card-${client.id}`}
          >
            <div className="client-card-icon">
              {type === "commercial" ? <Building2 size={28} /> : <Home size={28} />}
            </div>
            <div className="client-card-info">
              <h3 className="client-card-name">{client.company_name}</h3>
              <p className="client-card-location">{client.city}, {client.country}</p>
              <p className="client-card-contact">{client.contact_name}</p>
            </div>
            <ChevronRight size={20} className="client-card-arrow" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="client-selector" data-testid="client-selector">
      <div className="page-header">
        <h2 className="page-title">Select Client</h2>
        <p className="page-subtitle">
          {user?.role === "admin"
            ? "As Admin, you can view all clients"
            : "Select a client to work on their audit"}
        </p>
      </div>

      <div className="client-columns">
        {/* ---- COMMERCIAL CLIENTS ---- */}
        <div className="client-type-section" data-testid="commercial-clients-section">
          <div className="client-type-header">
            <div className="client-type-label">
              <Briefcase size={20} />
              <h3>Commercial Audits</h3>
              <span className="badge badge-neutral">{commercialClients.length}</span>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openCreate("commercial")}
              data-testid="create-commercial-client-btn"
            >
              <Plus size={16} /> Add New Client
            </button>
          </div>
          {renderClientGrid(commercialClients, "commercial")}
        </div>

        {/* ---- RESIDENTIAL CLIENTS ---- */}
        <div className="client-type-section" data-testid="residential-clients-section">
          <div className="client-type-header">
            <div className="client-type-label">
              <Home size={20} />
              <h3>Residential Audits</h3>
              <span className="badge badge-neutral">{residentialClients.length}</span>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openCreate("residential")}
              data-testid="create-residential-client-btn"
            >
              <Plus size={16} /> Add New Residence
            </button>
          </div>
          {renderClientGrid(residentialClients, "residential")}
        </div>
      </div>

      {/* ---- CREATE CLIENT MODAL ---- */}
      {showCreateModal && (
        <div className="modal-overlay" data-testid="create-client-modal">
          <div className="modal" style={{ maxWidth: "600px" }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {createType === "commercial" ? <Briefcase size={20} style={{ marginRight: "8px", verticalAlign: "middle" }} /> : <Home size={20} style={{ marginRight: "8px", verticalAlign: "middle" }} />}
                Add New {createType === "commercial" ? "Commercial Client" : "Residence"}
              </h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger mb-md">
                    <AlertCircle size={18} /><span>{error}</span>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label required">{createType === "commercial" ? "Company Name" : "Property Name"}</label>
                  <input type="text" className="form-input" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} required data-testid="client-company-name" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Address</label>
                    <input type="text" className="form-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">City</label>
                    <input type="text" className="form-input" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Zip Code</label>
                    <input type="text" className="form-input" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Country</label>
                    <select className="form-select" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} required>
                      <option value="">-- Select --</option>
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Contact Name</label>
                    <input type="text" className="form-input" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Phone</label>
                    <input type="tel" className="form-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Email</label>
                    <input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Website</label>
                    <input type="url" className="form-input" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading} data-testid="save-client-btn">
                  {loading ? "Creating..." : "Create Client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
